import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import Datastore from "../../communication/Datastore";
import store from "../../state/Store";
import LogArchive from "../../types/system/LogArchive";

// Define the type for our state store entry
type LogArchiveState = {
    data: LogArchive[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: LogArchiveState = {
    data: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const logArchiveSlice = createSlice({
    name: "logarchive",
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                return {
                    data:
                        rawData?.payload?.logArchives?.data?.map(
                            item => new LogArchive(item),
                        ) ?? [],
                    status:
                        !rawData?.payload?.logArchives?.data ||
                        !rawData?.payload?.logArchives?.data.length
                            ? "idle"
                            : rawData?.payload?.logArchives?.status,
                    error: null,
                };
            })
            .addCase(fetchLogDetails.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchLogDetails.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.data = action.payload[0].map(
                    record => new LogArchive(record),
                );
            })
            .addCase(fetchLogDetails.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

// This is a thunk, they are used to load the state
const fetchLogDetails = createAsyncThunk(
    "logarchive/fetchLogDetails",
    async () => {
        const response = await Promise.all([
            Datastore.getType<LogArchive[]>("api/Schools/LogArchives"),
        ]);
        return response;
    },
);
export const initialiseLogArchiveDetails = createAsyncThunk(
    "logarchive/initialiseLogArchiveDetails",
    async (_, thunkAPI) => {
        const { logArchives } = store.getState().db;

        if (logArchives.status === "idle")
            await thunkAPI.dispatch(fetchLogDetails());

        return store.getState().db.logArchives.data;
    },
);
export const downloadLogArchives = createAsyncThunk(
    "logarchive/downloadLogArchives",
    async (archives: LogArchive[], thunkAPI): Promise<string[]> => {
        return await Promise.all(
            archives.map(record =>
                Datastore.get(`api/Schools/LogArchives/${record.name}`),
            ),
        );
    },
);

// We have to export the reducer as this is added to the state store
export default logArchiveSlice.reducer;
